import type LayerViewModel from "@esWebAPI/es/map/LayerViewModel";
import type { Attribute } from "@esWebAPI/es/model/Feature";
import type { Criteria } from "@esWebAPI/es/model/Criteria";
import type FormatterFuncs from "@esWebAPI/es/util/formatterFuncs";

import AbstractFilterControl from "./AbstractFilterControl";
import FilterControlFactory from "../filterControlFactory";

export default class OrFilterControl extends AbstractFilterControl {
    private readonly filters: AbstractFilterControl[];

    constructor(layerViewModel: LayerViewModel, filters?: AbstractFilterControl[]) {
        super(layerViewModel, "Or");

        this.filters = filters || [];
        this.filters.forEach( f=> this.addFilterListeners(f));
    }

    private addFilterListeners(filter: AbstractFilterControl) {
        filter.getEventChannel().addListener("onUpdate", () => {
            this.eventChannel.fireEvent("onUpdate")
        });

        filter.getEventChannel().addListener("onDestroy", (f: AbstractFilterControl) => {
            this.removeFilter(f);
        });
    }

    toCriteria(expand?: boolean): Criteria | null {
        const criteria = this.filters.map(f => f.toCriteria(expand)).filter(c => c != null);
        
        if (criteria.length == 0) {
            return null;
        }
        
        if (criteria.length == 1) {
            return criteria[0];
        }
        
        // If a children is an 'All' we can return it directly
        const all = criteria.find(c => c.$type === "All");
        if (all) {
            return all;
        }
        
        return {
            $type: "Or",
            criteria: criteria
        };
    }

    fromCriteria(criteria: Criteria, attributes, range, layerModule, analysis, formatterFuncs: FormatterFuncs): JQueryDeferred<OrFilterControl> {
        criteria = es.control.filterUtil.groupByAttribute([criteria]);
        let dfds = [];
        criteria.forEach(c => {
            const attrs = es.control.filterUtil.getAttributesForCriteria(attributes, c);
            dfds.push(FilterControlFactory
                .fromCriteria(this.layerViewModel, c, attrs, range, layerModule, analysis, formatterFuncs)
                .then((filter) => this.addFilter(filter, true)));
        });

        return $.when.apply($, dfds).then(() => this);
    }

    setRange(range) {
        this.filters.forEach(f => f.setRange(range));
    }

    removeFilter(filter: AbstractFilterControl, silent = false) {
        const index = this.filters.indexOf(filter);
        if (index >= 0) {
            this.filters.splice(index, 1);
        }

        if (this.filters.length === 0) {
            this.destroy();
            return;
        }

        if (!silent) {
            this.eventChannel.fireEvent("onUpdate")
        }
    }

    addFilter(filter: AbstractFilterControl, silent = false) {
        if (filter.getType() === "Or") {
            throw new Error("OrFilter doesn't support nested Or filters.");
        }

        this.addFilterListeners(filter);
        this.filters.push(filter);

        if (!silent) {
            this.eventChannel.fireEvent("onUpdate")
        }
    }

    getFilters() {
        return this.filters;
    }

    hasFilter(filter: AbstractFilterControl) {
        return this.filters.indexOf(filter) >= 0;
    }

    getAttributesNames(): string[] {
        return this.filters.reduce((used, filter) => {
            if (filter.getType() === "And" || filter.getType() === "Or") {
               Array.prototype.push.apply(used, filter.getAttributesNames());
            } else if (filter.getAttrName()) {
                used.push(filter.getAttrName());
            }
            return used;
        }, [] as string[]);
    };

    getDynamicAttributes(): JQueryDeferred<Attribute[]> {
        const dfds = this.filters.reduce((used, filter) => {
            used.push(filter.getDynamicAttributes());
            return used;
        }, [] as JQueryDeferred<Attribute[]>[]);

        return $.when.apply($, dfds).then(function() {
            function pushRecursive(fromArray, toArray) {
                if (fromArray.length === 0) {
                    return;
                }

                fromArray.forEach(function (element) {
                    if (Array.isArray(element)) {
                        pushRecursive(element, toArray);
                    } else {
                        toArray.push(element);
                    }
                });
            }

            return Array.prototype.slice.call(arguments).reduce(function(arr, dynamicAttribute) {
                pushRecursive(dynamicAttribute, arr);
                return arr;
            }, []);
        });
    }


    static supportCriteria(criteria: Criteria): boolean {
        return criteria.$type === "Or" && _.uniq(criteria.criteria.map(c => c.attr)).length >= 2;
    }

    static supportAttribute() {
        return false;
    }

    static fromCriteria(layerViewModel: LayerViewModel, criteria: Criteria, attributes: Attribute | Attribute[], range, layerModule, analysis, formatterFuncs: FormatterFuncs): JQueryDeferred<OrFilterControl> {
        if (!Array.isArray(attributes)) {
            attributes = [attributes];
        }

        return new OrFilterControl(layerViewModel, []).init().then((filter: OrFilterControl) => {
            return filter.fromCriteria(criteria, attributes, range, layerModule, analysis, formatterFuncs);
        });
    }

    // es.control.OrFilter = OrFilter;

}
